import { ArrowBack, ArrowDropDown, ArrowDropUp, Clear, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { uniq } from 'lodash';
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { paperProps, StyledPaper } from '../constants/styles';
import { t } from '../constants/translations';
import { useLocale } from '../contexts/locale.context';
import useDebounce from '../utils/useDebounce';
import useMedia from '../utils/useMedia';
import PrimaryButton from './PrimaryButton';
import StyledTextField from './styled/StyledTextField';

export type baseRow = {
  id: string;
  order?: number;
};

export type Column<T extends baseRow> = {
  name: string;
  title: string;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  cellProps?: TableCellProps;
  filterable?: boolean;
  hideOnMobile?: boolean;
  sort?: (rows: T[]) => T[];
  sx?: SxProps;
};

type Props<T extends baseRow> = {
  title?: string;
  rows: T[];
  footerRows?: T[];
  columns: Column<T>[];
  groupBy?: keyof T;
  actionButton?: ReactNode;
  customHeader?: ReactNode;
  searchBy?: keyof T;
  onRowClick?: (row: T) => void;
  onGoBack?: () => void;
  onOrderChange?: (row: T, direction: 'up' | 'down') => Promise<void>;
  perPage?: number;
  isCard?: boolean;
  showHeader?: boolean;
  isGrid?: boolean;
  size?: 'small' | 'medium';
  showActionButtonOnEmpty?: boolean;
  dataTestIdPrefix?: string;
};

const DataTable = <T extends baseRow>({
  title,
  rows,
  footerRows,
  columns: columnsData,
  actionButton,
  searchBy,
  customHeader,
  perPage,
  isCard = true,
  showHeader = true,
  onRowClick,
  onGoBack,
  onOrderChange,
  isGrid,
  size,
  groupBy,
  showActionButtonOnEmpty = true,
  dataTestIdPrefix = '',
}: Props<T>) => {
  const { lang } = useLocale();
  const { isMobile } = useMedia();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [disabledOrder, setDisabledOrder] = useState(false);
  const [isReverse, setIsReverse] = useState(false);
  const debouncedSearch = useDebounce(search, 400);
  const minOrder = rows[0]?.order;
  const maxOrder = rows[rows.length - 1]?.order;

  const columns = useMemo(
    () => columnsData.filter((c) => c.render !== undefined && (!isMobile || !c.hideOnMobile)),
    [columnsData, isMobile]
  );

  const [sortedByIndex, setSortedByIndex] = useState(columns.findIndex((c) => !!c.sort));

  const filterableColumns = useMemo(() => columns.filter((c) => c.filterable), [columns]);

  const filterableColumnsMap = useMemo(
    () =>
      columns.reduce<Record<string, Column<T>>>(
        (acc, col) => ({
          ...acc,
          [col.name]: col,
        }),
        {}
      ),
    [columns]
  );

  const initialFilters = useMemo(() => {
    const filter: { [key in string]: string } = {};

    filterableColumns.forEach((col) => {
      filter[col.name] = '';
    });

    return filter;
  }, [filterableColumns]);

  const [filters, setFilters] = useState(initialFilters);

  const filtersOptions = useMemo(() => {
    const filtersOptions: { [key in string]: string[] } = {};

    filterableColumns.forEach((column) => {
      const options = uniq(
        rows
          .map((row) => {
            const value = column.render ? column.render(row) : null;

            return typeof value === 'string' ? value : (row[column.name as keyof T] as string);
          })
          .filter((v) => !!v)
      );
      filtersOptions[column.name] = options || [];
    });

    return filtersOptions;
  }, [filterableColumns, rows]);

  const filteredRows = useMemo(
    () =>
      rows.filter((row) =>
        Object.entries(filters).every(([field, value]) => {
          const column = filterableColumnsMap[field];
          const renderedValue = column && column?.render && column.render(row);
          return !value || renderedValue === value || row[field as keyof T] === value;
        })
      ),
    [rows, filters, filterableColumnsMap]
  );

  const searchedRows = useMemo(
    () =>
      searchBy
        ? filteredRows.filter(
            (row) =>
              (typeof row[searchBy] === 'string' || typeof row[searchBy] === 'number') &&
              (String(row[searchBy]) as string).toLocaleLowerCase().includes(debouncedSearch.toLocaleLowerCase())
          )
        : filteredRows,
    [filteredRows, debouncedSearch, searchBy]
  );

  const sortedRows = useMemo(() => {
    const sort = columns[sortedByIndex]?.sort;
    if (sort) {
      return isReverse ? sort(searchedRows) : sort(searchedRows).reverse();
    }
    return searchedRows;
  }, [columns, isReverse, searchedRows, sortedByIndex]);

  const hasPagination = useMemo(() => perPage && sortedRows.length > perPage, [sortedRows.length, perPage]);

  const slicedRows = useMemo(
    () => (perPage ? sortedRows.slice(page * perPage, (page + 1) * perPage) : sortedRows),
    [page, perPage, sortedRows]
  );

  const groupedRows = useMemo(() => {
    const groupedRows: Record<string, T[]> = {};
    if (groupBy) {
      slicedRows.forEach((row) => {
        const key = String(row[groupBy]);
        const prev = groupedRows[key];
        groupedRows[key] = [...(prev || []), row];
      });
    } else {
      groupedRows.unknonw = slicedRows;
    }
    return groupedRows;
  }, [groupBy, slicedRows]);

  const hasTop = useMemo(
    () => Boolean(onGoBack || customHeader || searchBy || Object.values(filters).length || actionButton),
    [onGoBack, customHeader, searchBy, filters, actionButton]
  );

  useEffect(() => {
    if (rows) {
      setPage(0);
    }
  }, [rows]);

  if (isGrid) {
    return (
      <Grid container data-testid={`${dataTestIdPrefix}-list`}>
        {slicedRows.map((r) => (
          <Grid
            item
            key={r.id}
            xs={4}
            sm={6}
            md={4}
            lg={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              alignSelf: 'flex-start',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 0.5,
              p: 1,
              borderRadius: 1,
              cursor: 'pointer',
              ':hover': {
                bgcolor: (t) => `${t.palette.primary.light}11`,
              },
            }}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            data-testid={`${dataTestIdPrefix}-list-item-${r.id}`}
          >
            {columns.map((col) => (col.render ? <div key={col.name}>{col.render(r)}</div> : null))}
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, ml: 2 }}>
          {title}
        </Typography>
      )}

      {hasTop && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {onGoBack && (
              <IconButton onClick={onGoBack}>
                <ArrowBack />
              </IconButton>
            )}

            {customHeader}

            {searchBy && (
              <StyledTextField
                label={t.search[lang]}
                name="search"
                value={search}
                onChange={(e) => {
                  setPage(0);
                  setSearch(e.target.value);
                }}
                size="small"
                InputProps={{ endAdornment: <Search /> }}
                sx={{ width: 202 }}
              />
            )}

            {Object.entries(filters).map(([field, value]) => (
              <div key={field}>
                {filtersOptions[field]!.length > 0 && (
                  <>
                    <StyledTextField
                      select
                      name={field}
                      label={t[field as keyof typeof t][lang]}
                      value={value}
                      size="small"
                      sx={{ width: 202 }}
                      SelectProps={{ MenuProps: { PaperProps: { sx: paperProps } } }}
                      onChange={(e) => {
                        setPage(0);
                        setFilters((prev) => ({ ...prev, [field]: e.target.value }));
                      }}
                    >
                      {filtersOptions[field]?.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </StyledTextField>

                    {value && (
                      <IconButton
                        sx={{ ml: 0.5 }}
                        onClick={() => {
                          setPage(0);
                          setFilters((prev) => ({ ...prev, [field]: '' }));
                        }}
                      >
                        <Clear />
                      </IconButton>
                    )}
                  </>
                )}
              </div>
            ))}
          </Box>

          {actionButton}
        </Box>
      )}

      <StyledPaper isCard={isCard}>
        {slicedRows.length === 0 && (
          <Box sx={{ textAlign: 'center', px: 5, py: 8 }}>
            <Typography sx={{ color: (t) => t.palette.text.disabled, mb: 2 }}>
              {debouncedSearch ? t.empty_search_message[lang] : t.there_is_nothing[lang]}
            </Typography>

            {debouncedSearch && (
              <PrimaryButton
                onClick={() => {
                  setSearch('');
                  setFilters(initialFilters);
                }}
              >
                {t.clear_filters[lang]}
              </PrimaryButton>
            )}

            {!debouncedSearch && showActionButtonOnEmpty && actionButton}
          </Box>
        )}
        <TableContainer>
          <Table sx={{ width: '100%' }} data-testid={`${dataTestIdPrefix}-list`}>
            {!!slicedRows.length && (
              <>
                {showHeader && (
                  <TableHead>
                    <TableRow>
                      {!!onOrderChange && rows.length > 1 && <TableCell sx={{ p: 0 }} />}

                      {columns.map((col, i) => {
                        const sorted = !!columns[i]?.sort;
                        return (
                          <TableCell key={col.name || undefined} align={col.align || 'right'} sx={col.sx}>
                            {sorted ? (
                              <Button
                                startIcon={
                                  // eslint-disable-next-line no-nested-ternary
                                  sortedByIndex === i ? (
                                    isReverse ? (
                                      <ArrowDropUp fontSize="small" />
                                    ) : (
                                      <ArrowDropDown fontSize="small" />
                                    )
                                  ) : undefined
                                }
                                size="small"
                                variant="text"
                                color="inherit"
                                onClick={() => (sortedByIndex === i ? setIsReverse((p) => !p) : setSortedByIndex(i))}
                              >
                                {col.title}
                              </Button>
                            ) : (
                              col.title
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                )}

                <TableBody>
                  {Object.entries(groupedRows).map(([group, rows]) => (
                    <Fragment key={group}>
                      {groupBy && (
                        <TableRow>
                          <TableCell size={size} colSpan={columns.length} sx={{ bgcolor: (t) => t.palette.grey[100] }}>
                            <Typography sx={{ ml: 2, color: (t) => t.palette.text.secondary }}>{group}</Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {rows.map((row) => (
                        <TableRow
                          key={row.id}
                          onClick={onRowClick ? () => onRowClick(row) : undefined}
                          sx={{
                            cursor: onRowClick ? 'pointer' : 'inherit',
                            ':hover': {
                              bgcolor: (t) => `${t.palette.primary.light}11`,
                            },
                          }}
                          data-testid={`${dataTestIdPrefix}-listItem-${row.id}`}
                        >
                          {!!onOrderChange && rows.length > 1 && (
                            <TableCell size={size} width={30} sx={{ py: 0, px: 0.5 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <IconButton
                                  size="small"
                                  disabled={disabledOrder || row.order === minOrder}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setDisabledOrder(true);
                                    await onOrderChange(row, 'up');
                                    setDisabledOrder(false);
                                  }}
                                >
                                  <ArrowDropUp fontSize="small" />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  disabled={disabledOrder || row.order === maxOrder}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOrderChange(row, 'down');
                                  }}
                                >
                                  <ArrowDropDown fontSize="small" />
                                </IconButton>
                              </Box>{' '}
                            </TableCell>
                          )}

                          {columns.map((col) => {
                            const value = col.render ? col.render(row) : null;

                            return (
                              <TableCell size={size} key={col.name} align={col.align || 'right'} sx={col.sx}>
                                {typeof value === 'string' || typeof value === 'number' ? (
                                  <Typography>{value}</Typography>
                                ) : (
                                  value
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}

                  {perPage && (
                    <TableRow>
                      <TablePagination
                        count={searchedRows.length}
                        rowsPerPage={perPage}
                        rowsPerPageOptions={[perPage]}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        labelDisplayedRows={({ page, count }) =>
                          hasPagination ? `${page + 1}/${Math.ceil(count / perPage)}` : undefined
                        }
                      />
                    </TableRow>
                  )}
                </TableBody>
              </>
            )}

            {footerRows && !debouncedSearch && (
              <TableFooter>
                {footerRows.map((footerRow, index) => (
                  <TableRow
                    key={footerRow.id}
                    sx={{
                      borderTop:
                        index === 0 || index === footerRows.length - 1
                          ? (t) => `2px solid ${t.palette.grey[300]}`
                          : undefined,
                    }}
                  >
                    {!!onOrderChange && rows.length > 1 && <TableCell sx={{ p: 0 }} />}

                    {columns.map((col) => {
                      const value = col.render ? col.render(footerRow) : null;

                      return (
                        <TableCell key={col.name} align={col.align || 'right'} sx={col.sx}>
                          {typeof value === 'string' || typeof value === 'number' ? (
                            <Typography
                              variant={index === footerRows.length - 1 ? 'h6' : 'subtitle1'}
                              color={(t) => t.palette.text.primary}
                            >
                              {value}
                            </Typography>
                          ) : (
                            value
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </StyledPaper>
    </>
  );
};

export default DataTable;
